import React, { useState, useEffect } from 'react';
import { Calendar, DollarSign, ListChecks, Clock, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { useAppSelector } from '../../store/hooks';
import { billingService, AdminBalanceDetailResponse, EventWithTransactions, BillingTransaction, ExtraServiceCreateData, ExtraServiceUpdateData, HostBalance } from '../../services/billingService';
import { useNotification } from '../../hooks/useNotification';
import TransactionTable from './TransactionTable';
import LoadingSpinner from '../UI/LoadingSpinner';
import { logError } from '../../utils/errorLogger';
import ExtraServiceForm from './ExtraServiceForm';
import ConfirmationModal from '../UI/ConfirmationModal';
import BillingActionPreview, { CostBreakdown } from './BillingActionPreview';
import Button from '../UI/Button';
import clsx from 'clsx';

interface AdminBalanceRowDetailProps {
  balanceId: number;
  onBalanceChange?: () => void; // Callback when balance changes (e.g., after adding extra service)
}

const AdminBalanceRowDetail: React.FC<AdminBalanceRowDetailProps> = ({ balanceId, onBalanceChange }) => {
  const { theme } = useAppSelector((state) => state.ui);
  const { showNotification } = useNotification();
  const [data, setData] = useState<AdminBalanceDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  
  // Extra service form state
  const [isExtraServiceFormOpen, setIsExtraServiceFormOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [editingTransaction, setEditingTransaction] = useState<BillingTransaction | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingServiceData, setPendingServiceData] = useState<ExtraServiceCreateData | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [balance, setBalance] = useState<HostBalance | null>(null);

  const loadDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const detailData = await billingService.getHostAdminDetail(balanceId);
      setData(detailData);
      setBalance(detailData.balance);
    } catch (err: any) {
      logError('Failed to load host detail', err);
      setError(err.message || 'Failed to load host details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDetail();
  }, [balanceId]);

  // Balance is already loaded from detail data, no need to fetch separately

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (timeString: string): string => {
    const [hourString, minuteString] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hourString, 10));
    date.setMinutes(parseInt(minuteString, 10));
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const toggleEvent = (eventId: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  const handleAddExtraService = (eventId: string) => {
    setSelectedEventId(eventId);
    setEditingTransaction(null);
    setIsExtraServiceFormOpen(true);
  };

  const handleEditExtraService = (transaction: BillingTransaction) => {
    setEditingTransaction(transaction);
    setSelectedEventId(transaction.related_event || null);
    setIsExtraServiceFormOpen(true);
  };

  const handleDeleteExtraService = async (transaction: BillingTransaction) => {
    if (!window.confirm(`Are you sure you want to delete the extra service "${transaction.service_name}"? This will refund ${Math.abs(parseFloat(transaction.amount)).toFixed(2)} JOD to the host.`)) {
      return;
    }

    try {
      await billingService.deleteExtraService(transaction.id);
      showNotification('Extra service deleted and refund issued', 'success');
      await loadDetail();
      // Notify parent to refresh balance list
      if (onBalanceChange) {
        onBalanceChange();
      }
    } catch (error: any) {
      logError('Failed to delete extra service', error);
      showNotification(
        error.response?.data?.error?.message || 'Failed to delete extra service',
        'error'
      );
    }
  };

  const handleFormSubmit = async (formData: ExtraServiceCreateData | ExtraServiceUpdateData) => {
    if (editingTransaction) {
      // Edit mode - update directly
      try {
        await billingService.updateExtraService(editingTransaction.id, formData as ExtraServiceUpdateData);
        showNotification('Extra service updated successfully', 'success');
        setIsExtraServiceFormOpen(false);
        setEditingTransaction(null);
        await loadDetail();
        // Notify parent to refresh balance list
        if (onBalanceChange) {
          onBalanceChange();
        }
      } catch (error: any) {
        logError('Failed to update extra service', error);
        showNotification(
          error.response?.data?.error?.message || 'Failed to update extra service',
          'error'
        );
      }
    } else {
      // Add mode - show confirmation with cost preview
      setPendingServiceData(formData as ExtraServiceCreateData);
      setIsExtraServiceFormOpen(false);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmAdd = async () => {
    if (!pendingServiceData) return;

    try {
      await billingService.addExtraService(pendingServiceData);
      showNotification('Extra service added successfully', 'success');
      setShowConfirmModal(false);
      setPendingServiceData(null);
      await loadDetail();
      // Notify parent to refresh balance list
      if (onBalanceChange) {
        onBalanceChange();
      }
    } catch (error: any) {
      logError('Failed to add extra service', error);
      showNotification(
        error.response?.data?.error?.message || 'Failed to add extra service',
        'error'
      );
    }
  };

  const calculateCosts = (): CostBreakdown[] => {
    if (!pendingServiceData) return [];
    const amount = parseFloat(pendingServiceData.amount);
    return [{
      label: pendingServiceData.service_name,
      amount: amount,
      quantity: 1,
      unitPrice: amount,
    }];
  };

  if (loading) {
    return (
      <div className={`p-6 border-t ${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="flex justify-center items-center py-8">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-6 border-t ${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
        <div className="text-center py-8">
          <p className={`text-sm ${theme === 'dark' ? 'text-red-400' : 'text-red-600'}`}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className={`p-6 border-t ${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <div className="space-y-6">
        {/* Balance Summary */}
        <div className={`rounded-lg border p-4 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Balance Summary
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Current Balance</p>
              <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {parseFloat(data.balance.balance).toFixed(2)} JOD
              </p>
            </div>
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Events</p>
              <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {data.balance.event_count}
              </p>
            </div>
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Transactions</p>
              <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {data.balance.transaction_count}
              </p>
            </div>
            <div>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Mirsal Member</p>
              <p className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                {data.balance.is_mirsal_member ? 'Yes' : 'No'}
              </p>
            </div>
          </div>
        </div>

        {/* Events List */}
        {data.events.length > 0 && (
          <div>
            <h3 className={`text-lg font-semibold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Events ({data.events.length})
            </h3>
            <div className="space-y-3">
              {(() => {
                // Group events by series
                const standaloneEvents: EventWithTransactions[] = [];
                const seriesGroups: Map<string, { main: EventWithTransactions | null; subEvents: EventWithTransactions[] }> = new Map();
                
                data.events.forEach((event: EventWithTransactions) => {
                  if (event.series_id) {
                    // Event is part of a series
                    if (event.is_main_event_in_series) {
                      // This is the main event
                      if (!seriesGroups.has(event.series_id)) {
                        seriesGroups.set(event.series_id, { main: event, subEvents: [] });
                      } else {
                        // Update main event if already exists
                        seriesGroups.get(event.series_id)!.main = event;
                      }
                    } else {
                      // This is a sub-event
                      if (!seriesGroups.has(event.series_id)) {
                        seriesGroups.set(event.series_id, { main: null, subEvents: [event] });
                      } else {
                        seriesGroups.get(event.series_id)!.subEvents.push(event);
                      }
                    }
                  } else {
                    // Standalone event
                    standaloneEvents.push(event);
                  }
                });
                
                // Sort sub-events by creation date
                seriesGroups.forEach((group) => {
                  group.subEvents.sort((a, b) => 
                    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                  );
                });
                
                // Render event card
                const renderEventCard = (event: EventWithTransactions, isSubEvent: boolean = false) => {
                  const isExpanded = expandedEvents.has(event.id);
                  return (
                    <div
                      key={event.id}
                      className={`rounded-lg border overflow-hidden transition-all ${
                        theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                      }`}
                    >
                      {/* Event Header - Always Visible */}
                      <div className={clsx(
                        'p-4 transition-colors',
                        theme === 'dark' ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
                        isExpanded && (theme === 'dark' ? 'border-b border-gray-700' : 'border-b border-gray-200')
                      )}>
                        <div 
                          onClick={() => toggleEvent(event.id)}
                          className="flex items-start justify-between cursor-pointer"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  {isSubEvent && (
                                    <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${
                                      theme === 'dark' 
                                        ? 'bg-blue-900/30 text-blue-400 border border-blue-700/50' 
                                        : 'bg-blue-50 text-blue-700 border border-blue-200'
                                    }`}>
                                      Sub-event
                                    </span>
                                  )}
                                  <h4 className={`text-base font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                    {event.title}
                                  </h4>
                                </div>
                                {isSubEvent && event.series_name && (
                                  <div className={`text-xs mt-1 ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    Part of: {event.series_name}
                                  </div>
                                )}
                              </div>
                              {isExpanded ? (
                                <ChevronUp className={`h-4 w-4 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                              ) : (
                                <ChevronDown className={`h-4 w-4 flex-shrink-0 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                              )}
                            </div>
                            <div className="mt-1 flex items-center text-sm gap-3">
                              <span className={`flex items-center gap-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                                <Calendar className="h-4 w-4" /> {formatDate(event.event_date)} at {formatTime(event.event_time)}
                              </span>
                            </div>
                          </div>
                          <div 
                            onClick={(e) => e.stopPropagation()}
                            className="flex items-center gap-4"
                          >
                            {event.transaction_count > 0 && (
                              <span className={`flex items-center gap-1 text-sm font-medium ${
                                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                              }`}>
                                <ListChecks className="h-4 w-4" /> {event.transaction_count} Transactions
                              </span>
                            )}
                            {parseFloat(event.total_deductions) > 0 && (
                              <span className={`flex items-center gap-1 text-sm font-medium text-red-600 dark:text-red-400`}>
                                <DollarSign className="h-4 w-4" /> -{parseFloat(event.total_deductions).toFixed(2)} JOD
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Transactions - Only visible when expanded */}
                      {isExpanded && (
                        <div 
                          onClick={(e) => e.stopPropagation()}
                          className="p-4 pt-0"
                        >
                          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                            {event.transactions.length > 0 && (
                              <TransactionTable
                                transactions={event.transactions}
                                showHostColumn={false}
                                showActions={true}
                                onEditExtraService={handleEditExtraService}
                                onDeleteExtraService={handleDeleteExtraService}
                              />
                            )}
                            <div className="mt-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAddExtraService(event.id)}
                                leftIcon={<Plus className="h-4 w-4" />}
                              >
                                Add Extra Service
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                };
                
                // Render events: standalone first, then series groups
                return (
                  <>
                    {standaloneEvents.map((event) => renderEventCard(event, false))}
                    {Array.from(seriesGroups.values()).map((group) => (
                      <div key={group.main?.id || group.subEvents[0]?.series_id}>
                        {group.main && renderEventCard(group.main, false)}
                        {group.subEvents.map((subEvent) => (
                          <div key={subEvent.id} className={`ml-6 border-l-2 ${
                            theme === 'dark' ? 'border-gray-700' : 'border-gray-300'
                          }`}>
                            {renderEventCard(subEvent, true)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Recent Activities */}
        {data.recent_activities.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Clock className={`h-5 w-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
              <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Recent Top-Ups ({data.recent_activities.length})
              </h3>
            </div>
            <TransactionTable
              transactions={data.recent_activities}
              showHostColumn={false}
            />
          </div>
        )}

        {data.events.length === 0 && data.recent_activities.length === 0 && (
          <div className={`text-center py-8 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              No events or transactions found for this host
            </p>
          </div>
        )}
      </div>

      {/* Extra Service Form Modal */}
      <ExtraServiceForm
        isOpen={isExtraServiceFormOpen}
        onClose={() => {
          setIsExtraServiceFormOpen(false);
          setEditingTransaction(null);
          setSelectedEventId(null);
        }}
        onSubmit={handleFormSubmit}
        mode={editingTransaction ? 'edit' : 'add'}
        initialData={editingTransaction}
        eventId={selectedEventId || undefined}
      />

      {/* Confirmation Modal with Cost Preview */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingServiceData(null);
        }}
        onConfirm={handleConfirmAdd}
        title="Add Extra Service"
        message={`You are about to add an extra service "${pendingServiceData?.service_name}" for ${pendingServiceData?.amount} JOD.`}
        confirmText="Add Service"
        cancelText="Cancel"
        confirmVariant="primary"
      >
        {!billingLoading && pendingServiceData && (
          <BillingActionPreview
            costs={calculateCosts()}
            showBalance={true}
            showBalanceAfter={true}
            currentBalance={balance}
            showWarning={true}
            compact={true}
            title="Cost Preview"
            infoText="This amount will be deducted from the host's balance immediately."
          />
        )}
      </ConfirmationModal>
    </div>
  );
};

export default AdminBalanceRowDetail;

